import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Printer } from 'lucide-react';
import { getRecycleOrderConsignmentAgreement } from 'apis/oms';
import XiaoMaLogoSvg from 'components/xiao-ma-logo-svg';
import {
  CONSIGNMENT_AGREEMENT,
  CONSIGNMENT_AGREEMENT_SNS_LABELS,
  CONSIGNMENT_AGREEMENT_UI,
} from 'pages/rrs/consignment-agreement/copy';
import { ConsignmentAgreementQuery } from 'types/oms';

dayjs.extend(utc);
dayjs.extend(timezone);

const JAPAN_TIMEZONE = 'Asia/Tokyo';

const PRINT_PAGE_STYLE = `
@media print {
  @page {
    size: A4 portrait;
    margin: 0;
  }
  .print-sheet {
    width: 210mm !important;
    padding: 0 !important;
    border-collapse: collapse;
  }
  .print-sheet td {
    padding: 0 !important;
    vertical-align: top;
  }
  .print-body {
    padding: 0 12mm !important;
  }
  .print-page-gap {
    height: 16mm;
  }
  .print-page-foot {
    height: 12mm;
  }
  thead {
    display: table-header-group;
  }
  tfoot {
    display: table-footer-group;
  }
  .print-keep {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  html, body, #root {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }
}
`;

const ConsignmentAgreementPrint = () => {
  const [searchParams] = useSearchParams();
  const submissionId = Number(searchParams.get('submissionId'));
  const validSubmissionId = Number.isInteger(submissionId) && submissionId > 0;
  const [data, setData] = useState<ConsignmentAgreementQuery | null>(null);
  const [loading, setLoading] = useState(true);
  const [imageReady, setImageReady] = useState(false);
  const signatureRef = useRef<HTMLImageElement | null>(null);
  const signUrl = data?.signed?.signUrl;

  useEffect(() => {
    if (!validSubmissionId) {
      setLoading(false);
      setImageReady(false);
      return;
    }
    let active = true;
    setLoading(true);
    setImageReady(false);
    getRecycleOrderConsignmentAgreement(submissionId)
      .then((res) => {
        if (!active) return;
        setData(res);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [submissionId, validSubmissionId]);

  useEffect(() => {
    const style = document.createElement('style');
    style.dataset.consignmentAgreementPrint = 'true';
    style.textContent = PRINT_PAGE_STYLE;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  useEffect(() => {
    if (loading || !data?.signed) return;
    if (!signUrl) {
      setImageReady(true);
      return;
    }

    const img = signatureRef.current;
    if (!img) return;

    let active = true;
    const markReady = () => {
      if (active) setImageReady(true);
    };
    const markReadyAfterDecode = () => {
      if (!active) return;
      if (img.naturalWidth > 0 && typeof img.decode === 'function') {
        img.decode().then(markReady).catch(markReady);
        return;
      }
      markReady();
    };

    // `complete` can be true before the browser starts the request. Only a
    // settled image (pixels decoded, or a broken response with currentSrc)
    // is safe to print.
    const settled =
      img.complete && (img.naturalWidth > 0 || Boolean(img.currentSrc));
    if (settled) {
      markReadyAfterDecode();
    } else {
      img.addEventListener('load', markReadyAfterDecode);
      img.addEventListener('error', markReady);
    }

    return () => {
      active = false;
      img.removeEventListener('load', markReadyAfterDecode);
      img.removeEventListener('error', markReady);
    };
  }, [data, loading, signUrl]);

  useEffect(() => {
    if (!data?.signed || !imageReady) return;
    const closePage = () => window.close();
    window.addEventListener('afterprint', closePage);
    const timer = window.setTimeout(() => window.print(), 300);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', closePage);
    };
  }, [data, imageReady]);

  const signed = data?.signed;
  const signedDate = signed?.signedAt
    ? dayjs(signed.signedAt).tz(JAPAN_TIMEZONE).format('YYYY年MM月DD日')
    : '';
  const snsText = (signed?.snsPlatforms ?? [])
    .map((platform) => CONSIGNMENT_AGREEMENT_SNS_LABELS[platform])
    .filter(Boolean)
    .join('、');

  return (
    <div className="min-h-screen bg-neutral-200 py-6 print:bg-white print:py-0">
      {loading ? (
        <div className="flex justify-center py-24">
          <Spin />
        </div>
      ) : !signed ? (
        <div className="mx-auto max-w-xl bg-white px-6 py-10 text-center">
          {CONSIGNMENT_AGREEMENT_UI.notReady}
        </div>
      ) : (
        <table
          className="print-sheet mx-auto box-border bg-white text-neutral-900 shadow-sm print:shadow-none"
          style={{ width: '210mm' }}
        >
          <thead>
            <tr>
              <td>
                <div className="print-page-gap" />
              </td>
            </tr>
          </thead>
          <tfoot>
            <tr>
              <td>
                <div className="print-page-foot" />
              </td>
            </tr>
          </tfoot>
          <tbody>
            <tr>
              <td>
                <div className="print-body" style={{ padding: '0 12mm' }}>
                  <header className="text-center">
                    <XiaoMaLogoSvg className="mx-auto h-14 w-auto" />
                    <div className="mt-4 text-xl font-medium tracking-wide">
                      {CONSIGNMENT_AGREEMENT.title}
                    </div>
                  </header>

                  <div className="mt-4 text-right text-sm">
                    {CONSIGNMENT_AGREEMENT_UI.bringInNo} {data?.submissionId}
                  </div>

                  <div className="mt-4 space-y-3 text-sm leading-6">
                    <p className="print-keep">
                      {CONSIGNMENT_AGREEMENT.preamble}
                    </p>
                    {CONSIGNMENT_AGREEMENT.articles.map((article) => (
                      <section key={article.no} className="print-keep">
                        <h2 className="font-medium">
                          {article.no}条 {article.title}
                        </h2>
                        {article.paragraphs.map((paragraph, index) => (
                          <p key={`${article.no}-${index}`}>{paragraph}</p>
                        ))}
                      </section>
                    ))}
                  </div>

                  <div className="print-keep mt-4 text-sm leading-6">
                    <div className="font-medium">
                      {CONSIGNMENT_AGREEMENT_UI.sns}
                    </div>
                    <div>{snsText}</div>
                  </div>

                  <div className="print-keep mt-8 grid grid-cols-2 items-start gap-10 text-sm leading-6">
                    <div>
                      <div>{CONSIGNMENT_AGREEMENT.footer.address}</div>
                      <div>{CONSIGNMENT_AGREEMENT.footer.tel}</div>
                      <div>{CONSIGNMENT_AGREEMENT.footer.company}</div>
                      <div className="mt-4 font-medium">
                        {CONSIGNMENT_AGREEMENT.footer.shopName}
                      </div>
                      <div className="mt-4 flex items-center gap-4">
                        <span>{CONSIGNMENT_AGREEMENT.footer.staff}</span>
                        <span>{CONSIGNMENT_AGREEMENT.footer.seal}</span>
                        <span className="inline-block h-16 w-16 border border-neutral-900" />
                      </div>
                    </div>
                    <div>
                      <div>{CONSIGNMENT_AGREEMENT.footer.printConsent}</div>
                      {signedDate && (
                        <div className="mt-4">
                          {CONSIGNMENT_AGREEMENT_UI.signedDate} {signedDate}
                        </div>
                      )}
                      <div className="mt-6">
                        {CONSIGNMENT_AGREEMENT.footer.signature}
                      </div>
                      <div className="mt-1 border-b border-neutral-900 pb-1">
                        {signed.signUrl && (
                          <img
                            ref={signatureRef}
                            src={signed.signUrl}
                            alt=""
                            className="max-h-24"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {signed && (
        <div className="fixed bottom-6 left-1/2 z-10 -translate-x-1/2 print:hidden">
          <Button
            className="h-12 px-8 text-base"
            icon={<Printer className="h-4 w-4" />}
            style={{
              backgroundColor: '#171717',
              borderColor: '#171717',
              color: '#fff',
            }}
            onClick={() => window.print()}
          >
            {CONSIGNMENT_AGREEMENT_UI.print}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConsignmentAgreementPrint;
